// UCP Readiness Analyzer - API Client
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL = 3000; // 3 seconds
const MAX_POLL_TIME = 180000; // 3 minutes

/**
 * Analyse a store for UCP readiness via backend API.
 * Uses async polling: POST starts analysis, then polls for results.
 */
export const analyzeStore = async (storeUrl, email, onProgress, forceRefresh = false) => {
  onProgress?.('Connecting to analysis service...');
  
  const url = forceRefresh 
    ? `${API}/analyse?force_refresh=true`
    : `${API}/analyse`;
  
  // Step 1: Start analysis (returns immediately)
  const startResponse = await axios.post(url, {
    url: storeUrl,
    email: email
  }, {
    timeout: 30000
  });
  
  const data = startResponse.data;
  
  // If we got a cached result back directly, return it
  if (data.overallScore !== undefined) {
    onProgress?.('Processing results...');
    return data;
  }
  
  // Step 2: We got a processing response — poll for completion
  const analysisId = data.analysisId;
  if (!analysisId) {
    throw new Error('Analysis failed to start. Please try again.');
  }
  
  onProgress?.('Analysing store structure...');
  
  const startTime = Date.now();
  
  while (Date.now() - startTime < MAX_POLL_TIME) {
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
    
    const elapsed = Math.round((Date.now() - startTime) / 1000);
    
    // Update progress messages based on elapsed time
    if (elapsed < 15) {
      onProgress?.('Fetching homepage...');
    } else if (elapsed < 30) {
      onProgress?.('Analysing product pages...');
    } else if (elapsed < 50) {
      onProgress?.('Checking policies and APIs...');
    } else if (elapsed < 70) {
      onProgress?.('Calculating scores...');
    } else {
      onProgress?.('Finalising analysis...');
    }
    
    try {
      const pollResponse = await axios.get(`${API}/analyses/${analysisId}`, {
        timeout: 10000
      });
      
      const result = pollResponse.data;
      
      if (result.status === 'complete' && result.overallScore !== undefined) {
        onProgress?.('Processing results...');
        return result;
      }
      
      if (result.status === 'failed') {
        throw new Error(result.error || 'Analysis failed. Please try again.');
      }
      
      // Still processing — continue polling
    } catch (pollError) {
      // 404 means not found yet, keep polling
      if (pollError.response?.status === 404) {
        continue;
      }
      // Network errors during polling are ok, keep trying
      if (!pollError.response) {
        continue;
      }
      throw pollError;
    }
  }
  
  throw new Error('Analysis is taking longer than expected. Please try again.');
};

export default analyzeStore;
